import React from 'react';
import PropTypes from 'prop-types';
import { PAGE_TOP_OFFSET } from "consts/styles";

import { alpha, styled } from '@mui/material/styles';
import { DataGridPro, gridClasses } from "@mui/x-data-grid-pro";

const EVEN_OPACITY = 0.06;

/**
 * 1st wrapper allows custom styling for data-grid.
 */
export const StyledDataGrid = styled(DataGridPro)(({
    // Unfortunately, palette etc. are not available when root component is not wrapped appropriately.
    // --> Fallback to default values taken from flow-core-components-shared/src/utils/muiDefaultTheme.json
    palette = {
        action: {selectedOpacity: 0.08, hoverOpacity: 0.04},
        background: {paper: '#fff'},
        primary: {main: '#007FFF'},
    },
    zIndex = {
        mobileStepper: 1000
    }
}) => ({
    ////// Variables //////
    "--DataGrid-pinnedBackground": "#fff",

    ////// Sticky Headers //////
    // --> https://stackoverflow.com/questions/66435092/material-ui-datagrid-sticky-header
    '& .MuiDataGrid-columnHeaders': {
        position: 'sticky',
        top: PAGE_TOP_OFFSET,
        backgroundColor: palette.background.paper,
        // Display header above grid data, but below any popups
        zIndex: zIndex.mobileStepper - 1
    },
    '& .MuiDataGrid-virtualScroller': {
        // Undo the margins that were added to push the rows below the previously fixed header.
        marginTop: '0 !important'
    },
    '& .MuiDataGrid-main': {
        // Overflow must be visible for sticky headers.
        overflow: 'visible'
    },

    ////// Striped Row Colors //////
    // --> https://mui.com/x/react-data-grid/style/#striped-rows
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: alpha(
            palette.primary.main,
            EVEN_OPACITY
        ),
        //backgroundColor: theme.palette.grey[200],
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(
                palette.primary.main,
                EVEN_OPACITY + palette.action.hoverOpacity
            ),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                palette.primary.main,
                EVEN_OPACITY + palette.action.selectedOpacity,
            ),
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                    palette.primary.main,
                    EVEN_OPACITY + palette.action.selectedOpacity + palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        palette.primary.main,
                        EVEN_OPACITY + palette.action.selectedOpacity,
                    ),
                },
            },
        },
        "& .MuiDataGrid-cell--pinnedLeft": {
            backgroundColor: "#f0f7ff",
        },
    },
    [`& .${gridClasses.row}.odd`]: {
        "& .MuiDataGrid-cell--pinnedLeft": {
            backgroundColor: "#fff",
        },
    },

    ////// Pinned columns //////
    "& .MuiDataGrid-columnHeader--pinnedLeft": {
        border: "none",
    },
    "& .MuiDataGrid-cell--pinnedLeft": {
        borderRight: "none",
    },
}));

/**
 * 2nd wrapper to customize props.
 * @param {boolean} striped
 * @param {object} rest
 * @return {*}
 * @constructor
 */
const EnhancedDataGrid = ({striped, ...rest}) => (
    <StyledDataGrid
        {...rest}
        getRowClassName={// Striped Row Colors --> https://mui.com/x/react-data-grid/style/#striped-rows
            striped
                ? (({indexRelativeToCurrentPage}) => indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')
                : undefined
        }
    />
);
EnhancedDataGrid.propTypes = {
    striped: PropTypes.bool
};

export default EnhancedDataGrid;
